import React from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaKpiCard, EaNote, EaPageHeader } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, MONTHS_IT, fmtQty, pointSub } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

function PointBlock({ p, year }) {
  const over = p.headroom_kw != null && p.headroom_kw < 0;
  const data = p.monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1], avail: p.available_power_kw }));
  return (
    <div className={`card-elevated p-5 space-y-4 ${over ? "!border-red-300" : ""}`} data-testid={`ea-pot-point-${p.identifier}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div><div className="font-display font-semibold text-slate-900">POD {p.identifier}</div><div className="text-xs text-slate-500">{pointSub(p)}</div></div>
        <EaExportButton filename={`power_potenza_${p.identifier}_${year}`} commodity="POWER" year={year} testid={`ea-pot-export-${p.identifier}`}
          columns={[{ key: "month", label: "Mese" }, { key: "max_kw", label: "Potenza max kW (quartorari)" }, { key: "report_max_kw", label: "Potenza max kW (Report)" }, { key: "avail", label: "Potenza disponibile kW" }, { key: "exceeded", label: "Superamento" }]}
          rows={data.map((r) => ({ ...r, identifier: p.identifier, address: p.address }))} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <EaKpiCard label="Potenza disponibile" value={fmtQty(p.available_power_kw, 0, "kW")} testid={`ea-pot-avail-${p.identifier}`} />
        <EaKpiCard label="Potenza massima annua" value={fmtQty(p.peak_kw, 0, "kW")} testid={`ea-pot-peak-${p.identifier}`} />
        <EaKpiCard label="Utilizzo" value={fmtQty(p.utilization_pct, 1, "%")} testid={`ea-pot-util-${p.identifier}`} />
        <EaKpiCard label="Headroom" value={<span className={over ? "text-red-600" : "text-green-700"}>{fmtQty(p.headroom_kw, 0, "kW")}</span>} testid={`ea-pot-headroom-${p.identifier}`} sub="Disponibile − massima" />
        <EaKpiCard label="Quarti d'ora oltre disponibile" value={p.exceed_quarters == null ? "—" : fmtQty(p.exceed_quarters, 0)} testid={`ea-pot-exceed-${p.identifier}`} />
      </div>
      {p.available_power_kw == null && <EaNote>Potenza disponibile non inserita: utilizzo, headroom e superamenti non calcolabili.</EaNote>}
      <div className="h-64" data-testid={`ea-pot-chart-${p.identifier}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="label" {...axis} />
            <YAxis {...axis} width={56} />
            <Tooltip formatter={(v, n) => [fmtQty(v, 0, "kW"), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="max_kw" name="Potenza max mensile" fill={COLORS.power} radius={[4, 4, 0, 0]}>{data.map((r) => <Cell key={r.month} fill={r.exceeded ? "#DC2626" : COLORS.power} />)}</Bar>
            {p.available_power_kw && <Line dataKey="avail" name="Potenza disponibile" stroke="#DC2626" strokeDasharray="5 3" dot={false} strokeWidth={2} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function EaPowerPower() {
  const codes = useSelectedCodes("POWER");
  const { data: d, loading, error } = useEaData("/ea/power/power-analysis", { codes });
  const header = <EaPageHeader title="Potenza" subtitle="Potenza disponibile, massima, utilizzo, superamenti e curva di durata del carico." right={<EaPointPicker commodity="POWER" />} />;
  const s = d?.stats || {};
  const refs = [["peak", "Picco", "#DC2626"], ["p95", "P95", "#F59E0B"], ["p90", "P90", "#EAB308"], ["median", "Mediana", "#0891B2"], ["baseload", "Baseload", "#16A34A"]];
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-power-power">
      {d && <>
        {!d.has_curves && <EaNote>Dati quartorari non disponibili.</EaNote>}
        {d.points.map((p) => <PointBlock key={p.code} p={p} year={d.year} />)}
        {d.duration_curve.length > 0 && (
          <EaChartCard title="Curva di durata del carico" subtitle={`Potenza quartoraria ordinata dalla maggiore alla minore · ${fmtQty(s.hours, 0)} ore di dati`} testid="ea-ldc-card"
            right={<EaExportButton filename={`power_curva_durata_${d.year}`} commodity="POWER" year={d.year} testid="ea-ldc-export"
              columns={[{ key: "hours", label: "Ore cumulate" }, { key: "pct", label: "% del tempo" }, { key: "kw", label: "Potenza kW" }]} rows={d.duration_curve} />}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {refs.map(([k, l]) => <EaKpiCard key={k} label={l} value={fmtQty(s[k], 0, "kW")} testid={`ea-ldc-${k}`} />)}
            </div>
            <div className="h-72" data-testid="ea-ldc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.duration_curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="pct" type="number" domain={[0, 100]} {...axis} tickFormatter={(v) => `${v}%`} />
                  <YAxis {...axis} width={56} />
                  <Tooltip formatter={(v) => [fmtQty(v, 0, "kW"), "Potenza"]} labelFormatter={(v, p) => `${fmtQty(v, 1)}% del tempo · ${fmtQty(p?.[0]?.payload?.hours, 0)} ore`} />
                  {refs.slice(1).map(([k, l, c]) => s[k] != null && <ReferenceLine key={k} y={s[k]} stroke={c} strokeDasharray="4 3" label={{ value: l, position: "right", fontSize: 10, fill: c }} />)}
                  <Line dataKey="kw" stroke={COLORS.power} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </EaChartCard>
        )}
      </>}
    </PageState>
  );
}
